import { create } from 'zustand';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

/** One press the toast offers beside its message — "Undo", never more than one. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  action?: ToastAction;
}

interface ToastState {
  toasts: Toast[];
  addToast: (type: ToastType, message: string, duration?: number, action?: ToastAction) => void;
  removeToast: (id: string) => void;
}

let toastCounter = 0;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  addToast: (type, message, duration = 4000, action) => {
    const id = `toast-${++toastCounter}-${Date.now()}`;
    set((state) => ({
      toasts: [...state.toasts.slice(-2), { id, type, message, duration, ...(action ? { action } : {}) }] // max 3 visible
    }));
    if (duration > 0) {
      setTimeout(() => {
        set((state) => ({ toasts: state.toasts.filter(t => t.id !== id) }));
      }, duration);
    }
  },
  removeToast: (id) => set((state) => ({ toasts: state.toasts.filter(t => t.id !== id) })),
}));

// Convenience helpers for use outside React components
export const toast = {
  // A toast with an action stays a little longer: the press is the point.
  success: (message: string, action?: ToastAction) =>
    useToastStore.getState().addToast('success', message, action ? 8000 : 4000, action),
  error: (message: string) => useToastStore.getState().addToast('error', message, 6000),
  warning: (message: string) => useToastStore.getState().addToast('warning', message),
  info: (message: string) => useToastStore.getState().addToast('info', message),
};
